import type { CredentialResponse } from '@react-oauth/google';

import { trackSignUp, trackStartTrial } from '@/analytics/gtag';
import { routeAfterAuthenticated } from '@/auth/constants/authSettings';
import useAuth from '@/auth/useAuth';
import defaultNotifications from '@/components/functions/defaultNotifications';
import { ErrorCode, GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { Capacitor } from '@capacitor/core';
import { Box, Button, LoadingOverlay, useComputedColorScheme } from '@mantine/core';
import { GoogleLogin } from '@react-oauth/google';
import { t } from 'i18next';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

/**
 * The web client ID is deliberate on native too: the plugin passes it as the `serverClientID`, which
 * Google returns as the ID token's `audience` — the single value the API verifies against. Runs on
 * every tap rather than being memoised, because initialize only stores the configuration and is
 * idempotent; a module-scoped cache silently survived test resets and hid the call after the first.
 */
function initializeNativeGoogleSignIn(): Promise<void> {
	const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
	if (!clientId) {
		return Promise.reject(new Error('VITE_GOOGLE_CLIENT_ID is required for Google Sign-In'));
	}

	return GoogleSignIn.initialize({ clientId });
}

function isCanceledGoogleSignIn(error: unknown): boolean {
	return typeof error === 'object' && error !== null && 'code' in error && error.code === ErrorCode.SignInCanceled;
}

type NativeGoogleSignInResult = 'success' | 'canceled' | 'error';

/**
 * Fire the one-time signup funnel events for a social (Google) sign-in. Mirrors RegisterPage:
 * sign_up on every new account; start_trial only when there's no invitation code, since invited
 * users join an existing space rather than starting their own free-plan trial. No-op for existing
 * users. gtag helpers are themselves no-ops off web, so this is safe to call on native too.
 */
// eslint-disable-next-line react-refresh/only-export-components -- exported alongside the component for direct unit testing of the funnel branching
export function fireSocialSignupEvents(isNewUser: boolean, invitationCode?: string): void {
	if (!isNewUser) {
		return;
	}

	trackSignUp('google');
	if (!invitationCode) {
		trackStartTrial({ plan: 'free' });
	}
}

function GoogleLogo() {
	return (
		<svg aria-hidden="true" viewBox="0 0 18 18" width="18" height="18">
			<path
				fill="#4285f4"
				d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.716v2.258h2.909c1.702-1.567 2.684-3.874 2.684-6.615Z"
			/>
			<path
				fill="#34a853"
				d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.258c-.806.54-1.836.859-3.047.859-2.344 0-4.329-1.585-5.037-3.711H.956v2.333A9 9 0 0 0 9 18Z"
			/>
			<path
				fill="#fbbc05"
				d="M3.963 10.71A5.41 5.41 0 0 1 3.682 9c0-.594.102-1.172.281-1.71V4.957H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.043l3.007-2.333Z"
			/>
			<path
				fill="#ea4335"
				d="M9 3.58c1.321 0 2.508.454 3.441 1.345l2.581-2.581C13.463.892 11.43 0 9 0A9 9 0 0 0 .956 4.957L3.963 7.29C4.671 5.164 6.656 3.58 9 3.58Z"
			/>
		</svg>
	);
}

async function performNativeGoogleSignIn(
	googleSignIn: (idToken: string, invitationCode?: string) => Promise<{ isNewUser: boolean }>,
	invitationCode: string | null,
): Promise<NativeGoogleSignInResult> {
	try {
		await initializeNativeGoogleSignIn();
		const { idToken } = await GoogleSignIn.signIn();
		const { isNewUser } = await googleSignIn(idToken, invitationCode || undefined);
		fireSocialSignupEvents(isNewUser, invitationCode || undefined);
		return 'success';
	} catch (error) {
		return isCanceledGoogleSignIn(error) ? 'canceled' : 'error';
	}
}

export default function GoogleLoginButton() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	// Both flags stay set after a successful sign-in on purpose: the router navigates in a
	// transition, so this button stays on screen until the next page has loaded, and clearing the
	// flag early left a dead-looking button that invited a second, racing sign-in.
	const [isNativeSignInPending, setIsNativeSignInPending] = useState(false);
	const [isWebSignInPending, setIsWebSignInPending] = useState(false);
	const redirectPath = searchParams.get('r') || routeAfterAuthenticated;
	const invitationCode = searchParams.get('code'); // Extract invitation code from URL

	const { googleSignIn } = useAuth();
	const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: false });

	const handleOnGoogleSuccess = async (credentialResponse: CredentialResponse) => {
		if (credentialResponse.credential) {
			const invitation = invitationCode || undefined;
			setIsWebSignInPending(true);
			try {
				const { isNewUser } = await googleSignIn(credentialResponse.credential, invitation);
				fireSocialSignupEvents(isNewUser, invitation);
				navigate(redirectPath, { replace: true });
			} catch {
				setIsWebSignInPending(false);
				// Show generic Google login error
				defaultNotifications.showError({
					message: t('login.notifications.googleError'),
				});
			}
		}
	};

	const handleOnGoogleError = () => {
		defaultNotifications.showError({
			message: t('login.notifications.googleError'),
		});
	};

	const handleNativeGoogleSignIn = async () => {
		if (isNativeSignInPending) {
			return;
		}

		setIsNativeSignInPending(true);
		const result = await performNativeGoogleSignIn(googleSignIn, invitationCode);

		if (result === 'success') {
			navigate(redirectPath, { replace: true });
			return;
		}

		setIsNativeSignInPending(false);
		if (result === 'error') {
			handleOnGoogleError();
		}
	};

	// Every native platform takes this path. The GIS widget below cannot work in a native webview:
	// its origin is capacitor://localhost (or the LAN dev server under live reload), and Google only
	// accepts http/https origins, so it always fails with origin_mismatch.
	if (Capacitor.getPlatform() !== 'web') {
		return (
			<Button
				variant="default"
				leftSection={<GoogleLogo />}
				loading={isNativeSignInPending}
				onClick={handleNativeGoogleSignIn}
				h={40}
				w={207}
				styles={{
					root: {
						// Google's button spec fixes a border colour per scheme.
						borderColor: 'var(--app-google-border)',
						borderRadius: 4,
						fontFamily: 'Roboto, arial, sans-serif',
						fontSize: 14,
						fontWeight: 500,
						paddingInline: 12,
					},
					section: { marginInlineEnd: 10 },
				}}
			>
				{t('login.signInWithGoogle')}
			</Button>
		);
	}

	// The Google widget is a cross-origin iframe with no disabled state, so an overlay is the only
	// way to both show progress and block a second click.
	return (
		<Box pos="relative">
			<LoadingOverlay visible={isWebSignInPending} loaderProps={{ size: 'sm' }} overlayProps={{ radius: 'sm' }} />
			<GoogleLogin
				onSuccess={handleOnGoogleSuccess}
				onError={handleOnGoogleError}
				// The widget is a cross-origin iframe, so CSS can't restyle it; only Google's own themes can.
				theme={colorScheme === 'dark' ? 'filled_black' : 'outline'}
				// The browser paints an opaque white backdrop behind an iframe whose document's colour
				// scheme differs from its embedder's; Google's document is always light.
				containerProps={{ style: { colorScheme: 'light' } }}
			/>
		</Box>
	);
}
